import React, { useMemo } from 'react';
import type { ColDef, EditableCallbackParams, ValueGetterParams, ValueSetterParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getCreditSourceGroupMap, saveCreditSourceGroupMapBatch } from '../../api/creditSourceGroupMap';
import type { CreditSourceGroupMap } from '../../types/CreditSourceGroupMap';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { useBalanceTypes } from '../../hooks/useBalanceTypes';
import { useCreditSourceGroups } from '../../hooks/useCreditSourceGroups';
import { t } from '../../i18n/t';

const editableOnAdd = (params: EditableCallbackParams<CreditSourceGroupMap>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

export function CreditSourceGroupMapPage() {
  const assetTypes = useAssetTypes();
  const balanceTypes = useBalanceTypes();
  const groups = useCreditSourceGroups();

  const columnDefs = useMemo<ColDef<CreditSourceGroupMap>[]>(() => {
    const astLabelById = new Map<number, string>();
    const astIdByLabel = new Map<string, number>();
    (assetTypes.data ?? []).forEach((a) => {
      const label = `${a.assetTypeId} - ${a.assetTypeName}`;
      astLabelById.set(a.assetTypeId, label);
      astIdByLabel.set(label, a.assetTypeId);
    });
    const astLabels = Array.from(astIdByLabel.keys());

    const balLabelById = new Map<number, string>();
    const balIdByLabel = new Map<string, number>();
    (balanceTypes.data ?? []).forEach((b) => {
      const label = `${b.balanceTypeId} - ${b.balanceTypeName}`;
      balLabelById.set(b.balanceTypeId, label);
      balIdByLabel.set(label, b.balanceTypeId);
    });
    const balLabels = Array.from(balIdByLabel.keys());

    const grpLabelById = new Map<number, string>();
    const grpIdByLabel = new Map<string, number>();
    (groups.data ?? []).forEach((g) => {
      const label = `${g.creditSourceGroupId} - ${g.creditSourceGroupName}`;
      grpLabelById.set(g.creditSourceGroupId, label);
      grpIdByLabel.set(label, g.creditSourceGroupId);
    });
    const grpLabels = Array.from(grpIdByLabel.keys());

    return [
      {
        field: 'assetTypeId',
        headerName: t('creditSourceGroupMap.columns.assetTypeId'),
        width: 260,
        cellDataType: 'text',
        editable: editableOnAdd,
        sort: 'asc',
        valueGetter: (p: ValueGetterParams<CreditSourceGroupMap>) => {
          const id = p.data?.assetTypeId;
          if (id == null) return '';
          return astLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<CreditSourceGroupMap>) => {
          const id = astIdByLabel.get(p.newValue as string);
          if (id == null) return false;
          (p.data as CreditSourceGroupMap).assetTypeId = id;
          return true;
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: astLabels },
      },
      {
        field: 'balanceTypeId',
        headerName: t('creditSourceGroupMap.columns.balanceTypeId'),
        width: 240,
        cellDataType: 'text',
        editable: editableOnAdd,
        valueGetter: (p: ValueGetterParams<CreditSourceGroupMap>) => {
          const id = p.data?.balanceTypeId;
          if (id == null) return '';
          return balLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<CreditSourceGroupMap>) => {
          const id = balIdByLabel.get(p.newValue as string);
          if (id == null) return false;
          (p.data as CreditSourceGroupMap).balanceTypeId = id;
          return true;
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: balLabels },
      },
      {
        field: 'creditSourceGroupId',
        headerName: t('creditSourceGroupMap.columns.creditSourceGroupId'),
        flex: 1,
        cellDataType: 'text',
        editable: true,
        valueGetter: (p: ValueGetterParams<CreditSourceGroupMap>) => {
          const id = p.data?.creditSourceGroupId;
          if (id == null) return '';
          return grpLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<CreditSourceGroupMap>) => {
          const id = grpIdByLabel.get(p.newValue as string);
          if (id == null) return false;
          (p.data as CreditSourceGroupMap).creditSourceGroupId = id;
          return true;
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: grpLabels },
      },
    ];
  }, [assetTypes.data, balanceTypes.data, groups.data]);

  const newRowDefaults = (): Partial<CreditSourceGroupMap> => ({ assetTypeId: 0, balanceTypeId: 0, creditSourceGroupId: 0 });

  return (
    <LopCrudPage<CreditSourceGroupMap>
      resourceCode="creditSourceGroupMap"
      titleKey="creditSourceGroupMap.pageTitle"
      idField="idKey"
      getRows={getCreditSourceGroupMap}
      saveBatch={(added, modified, deletedIds) => saveCreditSourceGroupMapBatch(added, modified, deletedIds as string[])}
      columnDefs={columnDefs}
      newRowDefaults={newRowDefaults}
    />
  );
}
